import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';

import * as service from '../services/availabilityWatch.service';
import { messages } from '../messages';

export const watchKeys = {
  mine: ['availability-watch', 'mine'] as const,
  forProfessional: (id: string) => ['availability-watch', 'professional', id] as const,
};

export function useMyAvailabilityWatches(enabled = true) {
  return useQuery({
    queryKey: watchKeys.mine,
    queryFn: () => service.listAvailabilityWatches(),
    staleTime: Infinity,
    enabled,
  });
}

export function useAvailabilityWatchActions() {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (professionalId: string) => service.createAvailabilityWatch(professionalId),
    onSuccess: (_data, professionalId) => {
      queryClient.invalidateQueries({ queryKey: watchKeys.mine });
      queryClient.invalidateQueries({ queryKey: watchKeys.forProfessional(professionalId) });
      toast.success(messages.toastWatchCreated);
    },
    onError: (err: unknown, professionalId) => {
      let code: string | undefined;
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as { error?: { code?: string } } | undefined;
        code = data?.error?.code;
      }
      if (code === 'PROFESSIONAL_ONLINE') {
        queryClient.invalidateQueries({ queryKey: ['professional', professionalId] });
        toast.info(messages.toastBackOnline);
      } else if (code === 'RATE_LIMITED' || (axios.isAxiosError(err) && err.response?.status === 429)) {
        toast.error(messages.toastRateLimited);
      } else {
        toast.error(messages.toastGenericError);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (professionalId: string) => service.deleteAvailabilityWatch(professionalId),
    onSuccess: (_data, professionalId) => {
      queryClient.invalidateQueries({ queryKey: watchKeys.mine });
      queryClient.invalidateQueries({ queryKey: watchKeys.forProfessional(professionalId) });
      toast.success(messages.toastWatchRemoved);
    },
    onError: () => {
      toast.error(messages.toastGenericError);
    },
  });

  return { createMutation, deleteMutation };
}
